import { apiFetch } from './http'

/**
 * The identity provider's self-service endpoints — the few things the SPA asks about the signed-in person
 * rather than about the platform.
 *
 * The identity provider is the only thing that knows them, but under the BFF this browser holds no token to
 * present to it — so the gateway asks on the session's behalf and relays the answer. Everything else in
 * `api/` talks to the platform; this is the exception, and it is small on purpose.
 */

/** One organisation the signed-in person may act in. */
export interface MyTenant {
  readonly id: string
  readonly slug: string
  readonly displayName: string
  readonly roles: readonly string[]
  /** Whether the token in hand acts in this tenant. Computed by the IdP, not inferred here. */
  readonly isCurrent: boolean
}

export interface MyTenants {
  readonly currentTenantId: string | null
  readonly tenants: readonly MyTenant[]
}

/**
 * The tenants this person may act in, from `GET /auth/tenants`.
 *
 * The list is the IdP's answer about the bearer, resolved through the same rule token issuance applies — so
 * every entry is one a sign-in would actually accept. The SPA does not filter or reorder it: a switcher that
 * decided for itself which memberships count would eventually disagree with the server, and the symptom would
 * be a tenant you can select and cannot enter.
 */
export async function fetchMyTenants(): Promise<MyTenants> {
  const response = await apiFetch('/api/identity/tenants')

  if (!response.ok) {
    throw new Error(`Could not read your organisations (HTTP ${response.status}).`)
  }

  return (await response.json()) as MyTenants
}
